import {
  boundUrlFromTemplate,
  canonicalBoundUrlTemplate,
  isAutoOpenGrantShape,
  isBoundUrlShape,
  normalizeBindableUrl,
  sourceClassAutoOpens,
  verifyAutoOpenGrant,
  verifyBoundUrl,
  type AutoOpenGrant,
  type BoundUrl,
  type BoundUrlPublicKeys,
  type UrlSourceClass,
} from "../security/boundUrl.ts";

/**
 * The client half of rule Q3 (docs/security/injection-quarantine.md): a URL
 * loads only when code chose its exact bytes. This session-wide set holds
 * every URL the client may load, and decides each load:
 *
 * - Bound: the server signed it for this user (`BoundUrl`, verified here
 *   with the public keys), code in this bundle names it (bindPlatformUrl,
 *   bindFirstPartyPath), or the user typed it (bindUserTypedUrl).
 * - A bound URL loads on a trusted user gesture (a real click, tap or key on
 *   the control that opens it), or once on an unused `AutoOpenGrant` the
 *   server minted for this turn. Anything else waits for a tap. A code
 *   constant from this bundle also opens on a spoken command: its bytes are
 *   ours, so no input can put data in them.
 * - An unbound URL never loads. It shows as plain, selectable text; a tap
 *   cannot override that. The user can type it into the address bar.
 *
 * Model output never reaches this set: model-written props carry no valid
 * signature, and the server strips `boundUrls` from anything a model wrote.
 */

export type { UrlSourceClass } from "../security/boundUrl.ts";

/**
 * The event of the user's own input on the control that opens a URL: a DOM
 * event, or a React event wrapping one. Only a browser-dispatched event
 * (`isTrusted`) counts; a script's `element.click()` or a voice command does not.
 */
export type UserGesture = Event | { readonly nativeEvent: Event };

/** Input events that are the user acting on a control. */
const GESTURE_EVENT_TYPES = new Set([
  "click",
  "auxclick",
  "dblclick",
  "pointerdown",
  "pointerup",
  "mousedown",
  "mouseup",
  "touchstart",
  "touchend",
  "keydown",
  "keyup",
  "submit",
]);

/** An event older than this is not the open's own input (Chromium's transient activation lasts as long). */
const GESTURE_MAX_AGE_MS = 5_000;

/** How long a viewer has to claim a load the user or a grant just allowed. */
const VIEWER_TICKET_TTL_MS = 30_000;

/** True when `gesture` is a fresh, browser-dispatched user input event. */
export function isTrustedGesture(gesture: UserGesture | null | undefined): boolean {
  if (!gesture || typeof Event === "undefined") return false;
  const event = gesture instanceof Event ? gesture : gesture.nativeEvent;
  if (!(event instanceof Event) || event.isTrusted !== true) return false;
  if (!GESTURE_EVENT_TYPES.has(event.type)) return false;
  if (typeof performance === "undefined" || !(event.timeStamp > 0)) return true;
  return performance.now() - event.timeStamp <= GESTURE_MAX_AGE_MS;
}

function appOrigin(): string | null {
  return typeof window !== "undefined" && window.location?.origin && window.location.origin !== "null"
    ? window.location.origin
    : null;
}

/**
 * The key a URL is bound and looked up by: the exact normalized https
 * address (normalizeBindableUrl), or this app's own origin for a
 * first-party page (which may be http in local development). Null for any
 * URL that can never bind.
 */
export function boundUrlKey(raw: unknown): string | null {
  if (typeof raw !== "string" || !raw) return null;
  const origin = appOrigin();
  let url: URL;
  try {
    url = origin ? new URL(raw, origin) : new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol === "https:") return normalizeBindableUrl(url.href);
  if (origin && url.origin === origin && !url.username && !url.password) return url.href;
  return null;
}

// Bound by this user's signed artifacts or typing; cleared when the user changes.
const userBound = new Map<string, UrlSourceClass>();
// The verified signed form of each server-bound URL, so a handoff to the
// desktop app can carry it (its window verifies it again). It always names
// the class userBound holds for that URL (see signedBoundUrl).
const signedBound = new Map<string, BoundUrl>();
// Verified templated bindings (BoundUrl.template), by class and template:
// every URL a template matches is bound under its class. Kept apart from
// userBound so a template never stands in for the exact signed URL.
const templateBound = new Map<string, BoundUrl>();
// Bound by code constants in this bundle; not user data, kept across users.
const codeBound = new Map<string, UrlSourceClass>();
// Verified, unused grants by reactId (one per user action).
const pendingGrants = new Map<string, AutoOpenGrant>();
// User actions whose grant was used. A grant opens once.
const usedGrantReactIds = new Set<string>();
// A load the user or a grant allowed, waiting for the viewer that shows it.
// `byUser`: the user's own input allowed it (a tap, typing, a click inside a
// page), not a grant or a code constant.
type ViewerTicket = { expires: number; byUser: boolean };
const viewerTickets = new Map<string, ViewerTicket[]>();
// The address each viewer window is allowed to show, and whether the user's own input allowed it.
const viewerLoads = new Map<string, { key: string; byUser: boolean }>();

/**
 * How far from code a source class is. A URL bound under two classes keeps
 * the farther one, so a link that also came in outside text keeps its
 * code-derived label (U5) and never counts as code's own.
 */
const SOURCE_DISTANCE: Record<UrlSourceClass, number> = {
  template_clean: 0,
  provider_canonical: 0,
  user_typed: 1,
  search_result: 2,
  outside_text: 3,
};

/** Binds `key` for this user under `sourceClass`; true when that changed the set. */
function bindForUser(key: string, sourceClass: UrlSourceClass): boolean {
  const current = userBound.get(key);
  if (current && SOURCE_DISTANCE[current] >= SOURCE_DISTANCE[sourceClass]) return false;
  userBound.set(key, sourceClass);
  return true;
}

let boundUser: string | null = null;
// Bumped when the user changes, so a verification that started before drops its result.
let generation = 0;
let version = 0;
const listeners = new Set<() => void>();
let loggedRejection = false;

function changed(): void {
  version += 1;
  for (const listener of listeners) listener();
}

let notifyQueued = false;

/**
 * Tells listeners after the current render: code bindings happen while a
 * component renders, and a listener's state update must not run inside
 * another component's render. The binding itself takes effect at once.
 */
function changedLater(): void {
  if (notifyQueued) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    changed();
  });
}

function logRejected(kind: string, count: number): void {
  if (count === 0 || loggedRejection) return;
  loggedRejection = true;
  console.warn(`[boundUrls] Dropped ${count} unverified ${kind}; further drops are not logged.`);
}

/** Calls `listener` whenever the set or a load decision changes; returns the unsubscribe. */
export function subscribeBoundUrls(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const tapListeners = new Set<(binding: BoundUrl) => void>();

/**
 * Calls `listener` with the server's signed binding each time the user's
 * own tap opens a server-bound URL, so the app can ask the server for a
 * grant that lets the next turn's agent fetch that page. Code-bound,
 * typed and grant-only URLs have no signed binding and never fire.
 */
export function subscribeBoundUrlTaps(listener: (binding: BoundUrl) => void): () => void {
  tapListeners.add(listener);
  return () => tapListeners.delete(listener);
}

function tapped(key: string): void {
  const binding = signedBoundUrl(key);
  if (!binding) return;
  for (const listener of tapListeners) {
    try {
      listener(binding);
    } catch (error) {
      console.warn("[boundUrls] A tap listener failed.", error);
    }
  }
}

/** Increases on every change; a snapshot for useSyncExternalStore. */
export function boundUrlsVersion(): number {
  return version;
}

/**
 * Scopes the set to the signed-in user. A different user (or sign-out)
 * clears every user binding, grant and allowed load, and drops any
 * verification still running for the previous user.
 */
export function setBoundUrlUser(userId: string | null): void {
  const next = userId || null;
  if (next === boundUser) return;
  boundUser = next;
  generation += 1;
  userBound.clear();
  signedBound.clear();
  templateBound.clear();
  pendingGrants.clear();
  usedGrantReactIds.clear();
  viewerTickets.clear();
  viewerLoads.clear();
  changed();
}

/** The user the set is scoped to, or null before sign-in. */
export function boundUrlUser(): string | null {
  return boundUser;
}

/**
 * Verifies `values` (the reserved `boundUrls` prop, or any value that may
 * hold `BoundUrl`s) for `userId` and adds each valid one. Invalid entries
 * are dropped silently (one log line per session). Resolves to the number added.
 */
export async function registerBoundUrls(
  values: unknown,
  userId: string,
  keys: BoundUrlPublicKeys,
): Promise<number> {
  if (!Array.isArray(values) || values.length === 0 || !userId) return 0;
  setBoundUrlUser(userId);
  const started = generation;
  const verdicts = await Promise.all(values.map((value) => verifyBoundUrl(value, userId, keys)));
  if (started !== generation) return 0;
  let added = 0;
  let rejected = 0;
  values.forEach((value, index) => {
    if (!verdicts[index] || !isBoundUrlShape(value)) {
      rejected += 1;
      return;
    }
    if (value.template) {
      const templateKey = `${value.sourceClass}\n${value.template.base}\n${canonicalBoundUrlTemplate(value.template)}`;
      if (!templateBound.has(templateKey)) {
        templateBound.set(templateKey, { ...value, url: value.template.base });
        added += 1;
      }
      return;
    }
    if (bindForUser(value.url, value.sourceClass)) added += 1;
    // Keep the signed form whose class is the one the URL is now bound
    // under, so a handoff never carries a nearer class than this window uses.
    if (userBound.get(value.url) === value.sourceClass) signedBound.set(value.url, value);
  });
  logRejected("bound URLs", rejected);
  if (added > 0) changed();
  return added;
}

/**
 * Verifies the `autoOpenGrants` of a live turn's action response. A grant
 * counts only for that turn (`reactId`), for this user, unexpired, never
 * for `outside_text` (an auto-loaded email link is a read receipt), and
 * only while its turn has no grant yet. A verified grant also binds its URL.
 * Never call this for restored history or a replay: those mint no grants.
 */
export async function registerAutoOpenGrants(
  values: unknown,
  turn: { userId: string; reactId: string },
  keys: BoundUrlPublicKeys,
  now: number = Date.now(),
): Promise<number> {
  if (!Array.isArray(values) || values.length === 0 || !turn.userId || !turn.reactId) return 0;
  setBoundUrlUser(turn.userId);
  const started = generation;
  const verdicts = await Promise.all(values.map((value) => verifyAutoOpenGrant(value, turn.userId, keys, now)));
  if (started !== generation) return 0;
  let added = 0;
  let rejected = 0;
  values.forEach((value, index) => {
    if (
      !verdicts[index]
      || !isAutoOpenGrantShape(value)
      || value.reactId !== turn.reactId
      // The client never knows whether the planner was isolated; the server
      // decides that. outside_text never auto-opens either way.
      || !sourceClassAutoOpens(value.sourceClass, true)
    ) {
      rejected += 1;
      return;
    }
    if (usedGrantReactIds.has(value.reactId) || pendingGrants.has(value.reactId)) return;
    pendingGrants.set(value.reactId, value);
    bindForUser(value.url, value.sourceClass);
    added += 1;
  });
  logRejected("auto-open grants", rejected);
  if (added > 0) changed();
  return added;
}

/** The farthest-from-code verified template that matches `key`, as a binding of `key`. */
function templateBindingFor(key: string): BoundUrl | null {
  let found: BoundUrl | null = null;
  for (const template of templateBound.values()) {
    if (found && SOURCE_DISTANCE[found.sourceClass] >= SOURCE_DISTANCE[template.sourceClass]) continue;
    found = boundUrlFromTemplate(template, key) ?? found;
  }
  return found;
}

/** The class this user's bindings give `key` (exact or by template), the farthest from code. */
function userSource(key: string): UrlSourceClass | null {
  const exact = userBound.get(key) ?? null;
  const templated = templateBindingFor(key)?.sourceClass ?? null;
  if (!exact || !templated) return exact ?? templated;
  return SOURCE_DISTANCE[templated] > SOURCE_DISTANCE[exact] ? templated : exact;
}

/** The source class `url` is bound with, or null when it is unbound. */
export function boundUrlSource(url: unknown): UrlSourceClass | null {
  const key = boundUrlKey(url);
  if (!key) return null;
  return userSource(key) ?? codeBound.get(key) ?? null;
}

/**
 * The server's signed binding of `url` under the class it is bound with
 * here, or null: code-bound, typed, grant-only, unbound, or bound here
 * under a farther class (a grant or typing) than any signature names. A
 * URL a bound template matches gets that template's binding with `url` set
 * to it; the server and the desktop verify it against the template again.
 */
export function signedBoundUrl(url: unknown): BoundUrl | null {
  const key = boundUrlKey(url);
  if (!key) return null;
  const source = userSource(key);
  if (!source) return null;
  const signed = signedBound.get(key);
  if (signed && signed.sourceClass === source) return signed;
  const templated = templateBindingFor(key);
  return templated && templated.sourceClass === source ? templated : null;
}

/**
 * Source classes whose links show a label code derives (the host plus where
 * the link came from), never the caller's text: a search result or a link
 * from an email could otherwise sit behind "View your bank statement".
 */
export function labelsFromCode(source: UrlSourceClass | null): source is "search_result" | "outside_text" {
  return source === "search_result" || source === "outside_text";
}

/** The host a link label names: the URL's hostname (punycode as is), without a leading `www.`. */
export function linkLabelHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/**
 * True when `url` is a code constant from this bundle (bindPlatformUrl,
 * bindFirstPartyPath): its bytes are ours, so the link check has nothing to
 * learn about it.
 */
export function isCodeBoundUrl(url: unknown): boolean {
  const key = boundUrlKey(url);
  return !!key && codeBound.has(key) && userSource(key) === null;
}

/** True when `url` is in the bound set (see boundUrlSource). */
export function isBoundUrl(url: unknown): boolean {
  return boundUrlSource(url) !== null;
}

/**
 * Binds a URL whose bytes are a code constant in this bundle: a platform
 * page, a vendor's billing or setup page. Never pass a value from props,
 * the network or a model: tests/ci/boundUrlBinders.test.ts requires a
 * string literal, or a file it lists with the constant's origin. Safe to
 * call while rendering.
 */
export function bindPlatformUrl(url: string): string | null {
  const key = boundUrlKey(url);
  if (!key || !key.startsWith("https:")) return null;
  if (!codeBound.has(key)) {
    codeBound.set(key, "template_clean");
    changedLater();
  }
  return key;
}

/**
 * Binds one of this app's own pages, by a path that is a code constant
 * (`/terms`, `/api/download?target=desktop`). Same rules as bindPlatformUrl.
 */
export function bindFirstPartyPath(path: string): string | null {
  const origin = appOrigin();
  if (!origin || !path.startsWith("/") || path.startsWith("//")) return null;
  const key = boundUrlKey(path);
  if (!key || new URL(key).origin !== origin) return null;
  if (!codeBound.has(key)) {
    codeBound.set(key, "template_clean");
    changedLater();
  }
  return key;
}

/**
 * The address the user entered and submitted (the address bar), or one they
 * chose to open on their own tap (a scanned code). Only the submit's or tap's
 * own trusted event binds it. It binds as `user_typed` only when the user
 * wrote every byte: an address that came from somewhere else names that
 * source in `from` (a scanned code, or a filled-in page address the user
 * edited rather than replaced) and keeps that class, so changing one
 * character of an outside address never makes it theirs.
 */
export function bindUserTypedUrl(raw: string, gesture: UserGesture, from: UrlSourceClass = "user_typed"): string | null {
  if (!isTrustedGesture(gesture) || !boundUser) return null;
  const key = boundUrlKey(raw);
  if (!key) return null;
  bindForUser(key, SOURCE_DISTANCE[from] > SOURCE_DISTANCE.user_typed ? from : "user_typed");
  addViewerTicket(key, true);
  changed();
  return key;
}

function grantFor(key: string, now: number): AutoOpenGrant | null {
  for (const grant of pendingGrants.values()) {
    if (grant.url !== key) continue;
    if (grant.exp > now && !usedGrantReactIds.has(grant.reactId)) return grant;
  }
  return null;
}

function spendGrant(grant: AutoOpenGrant): void {
  pendingGrants.delete(grant.reactId);
  usedGrantReactIds.add(grant.reactId);
}

function addViewerTicket(key: string, byUser: boolean, now: number = Date.now()): void {
  const live = (viewerTickets.get(key) ?? []).filter((ticket) => ticket.expires > now);
  live.push({ expires: now + VIEWER_TICKET_TTL_MS, byUser });
  viewerTickets.set(key, live);
}

function takeViewerTicket(key: string, now: number = Date.now()): ViewerTicket | null {
  const live = (viewerTickets.get(key) ?? []).filter((ticket) => ticket.expires > now);
  const taken = live.shift() ?? null;
  if (live.length > 0) viewerTickets.set(key, live);
  else viewerTickets.delete(key);
  return taken;
}

/**
 * Why a URL may (or may not) open now:
 * - `gesture`: bound, and the user's own input opened it.
 * - `grant`: bound, and this turn's unused grant opened it (now used).
 * - `code`: a code constant from this bundle (bindPlatformUrl,
 *   bindFirstPartyPath), opened by the app's own control without a tap, such
 *   as a spoken "get the app".
 * - `browsing`: a link the user clicked inside a page they are browsing
 *   (the desktop vouches for the click); that page could navigate there itself.
 * - `held`: bound, but nothing opened it yet: it waits for a tap.
 * - `unbound`: never loads; plain text only.
 */
export type UrlOpenDecision = "gesture" | "grant" | "code" | "browsing" | "held" | "unbound";

export function urlOpenAllowed(decision: UrlOpenDecision): boolean {
  return decision === "gesture" || decision === "grant" || decision === "code" || decision === "browsing";
}

export interface UrlOpenRequest {
  /** The user's own input event on the control that opens the URL. */
  gesture?: UserGesture | null;
  /**
   * Desktop only: the user clicked this link inside a page shown in a
   * WebViewer (the desktop consumed that click's activation).
   */
  browsing?: boolean;
  /** True when a WebViewer will show the page: an allowed open leaves it a ticket to claim. */
  forViewer?: boolean;
}

/**
 * An open of a URL, decided but not yet recorded (planUrlOpen). Nothing is
 * spent until commitUrlOpen, so a caller records it only once the page
 * actually opens.
 */
export interface UrlOpenPlan {
  readonly decision: UrlOpenDecision;
  readonly key: string | null;
  readonly grant: AutoOpenGrant | null;
}

/** Decides an open of `url` without recording anything (see authorizeUrlOpen). */
export function planUrlOpen(url: unknown, request: UrlOpenRequest = {}, now: number = Date.now()): UrlOpenPlan {
  const key = boundUrlKey(url);
  const plan = (decision: UrlOpenDecision, grant: AutoOpenGrant | null = null): UrlOpenPlan => ({ decision, key, grant });
  if (!key) return plan("unbound");
  if (request.browsing) return plan("browsing");
  if (!isBoundUrl(key)) return plan("unbound");
  if (isTrustedGesture(request.gesture)) return plan("gesture");
  if (codeBound.has(key)) return plan("code");
  const grant = grantFor(key, now);
  return grant ? plan("grant", grant) : plan("held");
}

/**
 * Records an allowed open that happened: its grant is spent, and with
 * `forViewer` the viewer that shows the page may claim it. Returns false,
 * recording nothing, when the plan does not allow an open, or its grant
 * was used since it was planned.
 */
export function commitUrlOpen(plan: UrlOpenPlan, request: UrlOpenRequest = {}, now: number = Date.now()): boolean {
  const { key, grant } = plan;
  if (!key || !urlOpenAllowed(plan.decision)) return false;
  if (grant) {
    if (usedGrantReactIds.has(grant.reactId) || pendingGrants.get(grant.reactId) !== grant) return false;
    spendGrant(grant);
  }
  if (plan.decision === "gesture") tapped(key);
  if (request.forViewer) addViewerTicket(key, plan.decision === "gesture" || plan.decision === "browsing", now);
  changed();
  return true;
}

/**
 * Decides an open of `url` and, when allowed, records it at once
 * (planUrlOpen then commitUrlOpen). For an open that can still fail after
 * the decision, plan and commit separately.
 */
export function authorizeUrlOpen(url: unknown, request: UrlOpenRequest = {}, now: number = Date.now()): UrlOpenDecision {
  const plan = planUrlOpen(url, request, now);
  if (!urlOpenAllowed(plan.decision)) return plan.decision;
  return commitUrlOpen(plan, request, now) ? plan.decision : "held";
}

/** True when viewer `viewerId` may show `url` now (pure; see claimViewerLoad). */
export function isViewerLoadAllowed(viewerId: string, url: unknown): boolean {
  const key = boundUrlKey(url);
  return !!key && viewerLoads.get(viewerId)?.key === key;
}

/**
 * True when viewer `viewerId` may show `url` because of the user's own
 * input (a tap, typing, a click inside a page they browse), not a grant or
 * a code constant. A page a check expanded to somewhere else opens by
 * itself only then; otherwise the user taps it open (linkGate.ts).
 */
export function isViewerLoadByUser(viewerId: string, url: unknown): boolean {
  const key = boundUrlKey(url);
  const load = key ? viewerLoads.get(viewerId) : undefined;
  return !!load && load.key === key && load.byUser;
}

/**
 * Called by a viewer about to show `url`: it may when an allowed open left
 * a ticket for it, when this turn's unused grant names it (the grant is
 * then used), or when it is a code constant. Once allowed, the viewer keeps
 * showing that address.
 */
export function claimViewerLoad(viewerId: string, url: unknown, now: number = Date.now()): boolean {
  const key = boundUrlKey(url);
  if (!key) return false;
  if (viewerLoads.get(viewerId)?.key === key) return true;
  const ticket = takeViewerTicket(key, now);
  if (!ticket && !codeBound.has(key)) {
    if (!isBoundUrl(key)) return false;
    const grant = grantFor(key, now);
    if (!grant) return false;
    spendGrant(grant);
  }
  viewerLoads.set(viewerId, { key, byUser: ticket?.byUser === true });
  changed();
  return true;
}

/** The user tapped the viewer's own load control for a bound address it holds. */
export function loadViewerOnGesture(viewerId: string, url: unknown, gesture: UserGesture): boolean {
  const key = boundUrlKey(url);
  if (!key || !isBoundUrl(key) || !isTrustedGesture(gesture)) return false;
  viewerLoads.set(viewerId, { key, byUser: true });
  tapped(key);
  changed();
  return true;
}

/** Test seam: forgets everything, including code bindings. */
export function resetBoundUrlsForTest(): void {
  userBound.clear();
  signedBound.clear();
  templateBound.clear();
  codeBound.clear();
  pendingGrants.clear();
  usedGrantReactIds.clear();
  viewerTickets.clear();
  viewerLoads.clear();
  boundUser = null;
  generation += 1;
  loggedRejection = false;
  changed();
}
